// Status normalizados exibidos ao coordenador na Central da Coordenação.
// O backend devolve statusDescricao (enum StatusNoticia) + flag autorizado;
// aqui isso vira um único status legível.
export const STATUS_NOTICIA_COORDENACAO = Object.freeze({
  ENVIADA: "ENVIADA",
  APROVADA: "APROVADA",
  PROGRAMADA: "PROGRAMADA",
  PUBLICADA: "PUBLICADA",
  RECUSADA: "RECUSADA",
  EM_EDICAO: "EM_EDICAO",
  EXCLUIDA: "EXCLUIDA",
});

export const STATUS_NOTICIA_COORDENACAO_LABEL = Object.freeze({
  [STATUS_NOTICIA_COORDENACAO.ENVIADA]: "Aguardando análise",
  [STATUS_NOTICIA_COORDENACAO.APROVADA]: "Aceita",
  [STATUS_NOTICIA_COORDENACAO.PROGRAMADA]: "Aceita",
  [STATUS_NOTICIA_COORDENACAO.PUBLICADA]: "Publicada",
  [STATUS_NOTICIA_COORDENACAO.RECUSADA]: "Rejeitada",
  [STATUS_NOTICIA_COORDENACAO.EM_EDICAO]: "Em edição",
  [STATUS_NOTICIA_COORDENACAO.EXCLUIDA]: "Excluída",
});

// Classe CSS do badge por status (mesma paleta dos badges da Central PREX)
export const STATUS_NOTICIA_COORDENACAO_CLASSE = Object.freeze({
  [STATUS_NOTICIA_COORDENACAO.ENVIADA]: "pendente",
  [STATUS_NOTICIA_COORDENACAO.APROVADA]: "aceita",
  [STATUS_NOTICIA_COORDENACAO.PROGRAMADA]: "programada",
  [STATUS_NOTICIA_COORDENACAO.PUBLICADA]: "publicada",
  [STATUS_NOTICIA_COORDENACAO.RECUSADA]: "rejeitada",
  [STATUS_NOTICIA_COORDENACAO.EM_EDICAO]: "neutra",
  [STATUS_NOTICIA_COORDENACAO.EXCLUIDA]: "neutra",
});

// Cards de resumo/filtro exibidos acima da listagem ("TODAS" = sem filtro)
export const FILTROS_STATUS_NOTICIA_COORDENACAO = Object.freeze([
  {
    valor: "TODAS",
    classe: "total",
    rotulo: "Total submetido",
    descricao: "Todas as notícias",
  },
  {
    valor: STATUS_NOTICIA_COORDENACAO.ENVIADA,
    classe: "pendente",
    rotulo: "Aguardando análise",
    descricao: "Aguardando análise",
  },
  {
    valor: STATUS_NOTICIA_COORDENACAO.PROGRAMADA,
    classe: "programada",
    rotulo: "Aceitas",
    descricao: "Autorizadas e aguardando publicação",
  },
  {
    valor: STATUS_NOTICIA_COORDENACAO.PUBLICADA,
    classe: "aprovada",
    rotulo: "Publicadas",
    descricao: "Já disponíveis no portal",
  },
  {
    valor: STATUS_NOTICIA_COORDENACAO.RECUSADA,
    classe: "reprovada",
    rotulo: "Rejeitadas",
    descricao: "Não autorizadas",
  },
]);

// Abrangência da notícia (enum AbrangenciaNoticia) → local onde ela é publicada
export const LOCAL_PUBLICACAO_NOTICIA = Object.freeze({
  ACAO: { label: "Na ação", emTexto: "na ação", icone: "ti ti-flag" },
  UNIDADE: {
    label: "Na unidade",
    emTexto: "na unidade",
    icone: "ti ti-building",
  },
  PREX: { label: "Na UFC", emTexto: "na UFC", icone: "ti ti-school" },
});

/**
 * Converte statusDescricao/autorizado vindos do backend em um dos
 * STATUS_NOTICIA_COORDENACAO.
 */
export function normalizarStatusNoticia(noticia) {
  if (!noticia) return STATUS_NOTICIA_COORDENACAO.ENVIADA;

  const status = String(
    noticia.statusDescricao ||
      noticia.statusNome ||
      noticia.tipoStatus ||
      noticia.status ||
      ""
  ).toUpperCase();

  // 1. Excluída / Cancelada / Em Edição
  if (status.includes("EXCLUID")) return STATUS_NOTICIA_COORDENACAO.EXCLUIDA;
  if (status.includes("EM_EDICAO") || status.includes("EDICAO"))
    return STATUS_NOTICIA_COORDENACAO.EM_EDICAO;

  // 2. Rejeitada explicitamente
  if (
    status.includes("NAO_AUTORIZAD") ||
    status.includes("REJEIT") ||
    status.includes("RECUSAD") ||
    status.includes("CANCEL")
  ) {
    return STATUS_NOTICIA_COORDENACAO.RECUSADA;
  }

  // Avaliação temporal da data de publicação
  const agora = new Date();
  const dataPub = noticia.dataPublicacao ? new Date(noticia.dataPublicacao) : null;
  const dataPubValida = dataPub instanceof Date && !isNaN(dataPub.getTime());
  const isDataFutura = dataPubValida && dataPub.getTime() > agora.getTime();

  // 3. Aguardando análise (PREX ou Unidade)
  if (status.includes("AGUARD") || status.includes("PENDENTE")) {
    if (noticia.autorizado === true) {
      return isDataFutura
        ? STATUS_NOTICIA_COORDENACAO.PROGRAMADA
        : STATUS_NOTICIA_COORDENACAO.PUBLICADA;
    }
    return STATUS_NOTICIA_COORDENACAO.ENVIADA;
  }

  // 4. Publicada
  if (status.includes("PUBLICAD")) {
    if (isDataFutura) {
      return STATUS_NOTICIA_COORDENACAO.PROGRAMADA;
    }
    return STATUS_NOTICIA_COORDENACAO.PUBLICADA;
  }

  // 5. Agendada / Programada / Aceita
  if (
    status.includes("AGENDAD") ||
    status.includes("PROGRAMAD") ||
    status.includes("ACEITA") ||
    status.includes("APROVADA")
  ) {
    if (dataPubValida && dataPub.getTime() <= agora.getTime()) {
      return STATUS_NOTICIA_COORDENACAO.PUBLICADA;
    }
    return STATUS_NOTICIA_COORDENACAO.PROGRAMADA;
  }

  // 6. Decisão baseada na flag autorizado (quando status for genérico)
  if (noticia.autorizado === true) {
    return isDataFutura
      ? STATUS_NOTICIA_COORDENACAO.PROGRAMADA
      : STATUS_NOTICIA_COORDENACAO.PUBLICADA;
  }
  if (noticia.autorizado === false && (noticia.motivoRejeicao || status.includes("NAO"))) {
    return STATUS_NOTICIA_COORDENACAO.RECUSADA;
  }

  return STATUS_NOTICIA_COORDENACAO.ENVIADA;
}

/**
 * Retorna { label, icone } do local de publicação a partir de abrangenciaNome.
 */
export function localPublicacaoNoticia(noticia) {
  const abrangencia = String(
    noticia?.abrangenciaNome || noticia?.abrangencia || ""
  ).toUpperCase();

  if (abrangencia.includes("PREX") || abrangencia.includes("UFC"))
    return LOCAL_PUBLICACAO_NOTICIA.PREX;
  if (abrangencia.includes("UNIDADE")) return LOCAL_PUBLICACAO_NOTICIA.UNIDADE;
  if (abrangencia.includes("ACAO")) return LOCAL_PUBLICACAO_NOTICIA.ACAO;
  return {
    label: "Local não informado",
    emTexto: "no portal",
    icone: "ti ti-map-pin",
  };
}
